import { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { Button, Card, CardContent, CardHeader, CardTitle } from "@/ui";
import { CheckCircle, Mail, ArrowLeft, FileText, Calendar, User } from 'lucide-react';

interface ApplicationData {
  applicationId: string;
  candidateName: string;
  jobTitle: string;
  appliedAt: string;
}

export default function ApplicationThankYou() {
  const [, setLocation] = useLocation();
  const [applicationData, setApplicationData] = useState<ApplicationData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get application data from URL parameters or localStorage
    const urlParams = new URLSearchParams(window.location.search);
    const applicationId = urlParams.get('applicationId');
    const candidateName = urlParams.get('candidateName');
    const jobTitle = urlParams.get('jobTitle');
    const appliedAt = urlParams.get('appliedAt');

    if (applicationId && candidateName && jobTitle) {
      setApplicationData({
        applicationId,
        candidateName,
        jobTitle,
        appliedAt: appliedAt || new Date().toISOString()
      });
    } else {
      // Try to get from localStorage as fallback
      const storedData = localStorage.getItem('lastApplicationData');
      if (storedData) {
        setApplicationData(JSON.parse(storedData));
      }
    }
    setLoading(false);
  }, []);

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Just now';
    }
  };

  if (loading) {
    return (
      <div>
        <main className="py-20">
          <div className="container mx-auto px-4 text-center">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-1/4 mx-auto mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!applicationData) {
    // Debug: Show what we received
    const urlParams = new URLSearchParams(window.location.search);
    const debugInfo = {
      applicationId: urlParams.get('applicationId'),
      candidateName: urlParams.get('candidateName'),
      jobTitle: urlParams.get('jobTitle'),
      fullUrl: window.location.href,
      search: window.location.search
    };
    
    return (
      <div>
        <main className="py-20">
          <div className="container mx-auto px-4 max-w-2xl">
            <Card className="text-center">
              <CardContent className="pt-6">
                <div className="text-red-500 mb-4">
                  <FileText className="h-16 w-16 mx-auto" />
                </div>
                <h1 className="text-2xl font-bold text-foreground mb-4">Application Not Found</h1>
                <p className="text-muted-foreground mb-6">
                  We couldn't find your application information. This might happen if you accessed this page directly.
                </p>
                <div className="text-left bg-gray-100 p-4 rounded mb-6 text-xs">
                  <p className="font-mono"><strong>Debug Info:</strong></p>
                  <pre className="mt-2">{JSON.stringify(debugInfo, null, 2)}</pre>
                </div>
                <Button onClick={() => setLocation('/careers')} className="mr-4">
                  View Open Positions
                </Button>
                <Button variant="outline" onClick={() => setLocation('/contact')}>
                  Contact Us
                </Button>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div>
      <main className="py-20">
        <div className="container mx-auto px-4 max-w-4xl">
          {/* Success Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            <h1 className="text-4xl font-bold text-foreground mb-4">
              Thank You for Your Application!
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              We've received your application and will review it carefully. We'll get back to you soon.
            </p>
          </div>

          {/* Application Details */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Application Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <User className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Candidate Name</p>
                      <p className="font-semibold">{applicationData.candidateName}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Application ID</p>
                      <p className="font-mono font-semibold text-primary">#{applicationData.applicationId}</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Calendar className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Applied On</p>
                      <p className="font-semibold">{formatDate(applicationData.appliedAt)}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="text-sm text-muted-foreground">Position Applied For</p>
                      <p className="font-semibold">{applicationData.jobTitle}</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Next Steps */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>What Happens Next?</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-semibold">
                    1
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Application Review</h4>
                    <p className="text-sm text-muted-foreground">
                      Our HR team will review your application and resume within 3-5 business days.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-semibold">
                    2
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Initial Screening</h4>
                    <p className="text-sm text-muted-foreground">
                      If your profile matches our requirements, we'll contact you for an initial phone screening.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-semibold">
                    3
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Interview Process</h4>
                    <p className="text-sm text-muted-foreground">
                      Selected candidates will be invited for technical and cultural fit interviews.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center text-sm font-semibold">
                    4
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Final Decision</h4>
                    <p className="text-sm text-muted-foreground">
                      We'll notify you of our decision and next steps within 2 weeks of your interview.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Information */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                Questions or Need Help?
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <p className="text-muted-foreground">
                  If you have any questions about your application or the hiring process, please don't hesitate to contact us:
                </p>
                <div className="bg-muted/50 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-2">
                    <Mail className="h-5 w-5 text-primary" />
                    <span className="font-semibold">HR Department</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-1">
                    Email: <a href="mailto:hr@aiscendus.com" className="text-primary hover:underline">hr@aiscendus.com</a>
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Please include your Application ID (#{applicationData.applicationId}) in your email for faster assistance.
                  </p>
                </div>
                <p className="text-sm text-muted-foreground">
                  We typically respond to inquiries within 24-48 hours during business days.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => setLocation('/careers')} variant="outline" className="flex items-center gap-2">
              <ArrowLeft className="h-4 w-4" />
              View Other Positions
            </Button>
            <Button onClick={() => setLocation('/')} className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Learn More About Aiscendus
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
